import { atom, atomFamily } from "recoil";

import { NotDraggingDataConst } from "../../constants";
import type {
  ILiveMapIncidentItem,
  IRideMetadata,
  TStationComposedId,
} from "../../dto";
import type {
  PassengerDeleteResultEnum,
  RebuildRouteTypeEnum,
  RidesKanbanSortEnum,
} from "../../enums";
import { getStoredRidesKanbanSort } from "../../helpers";
import type {
  IPassengerDeleteModalPayload,
  IRideArchiveModalPayload,
  IRideAreasSnapshot,
  IRideBookingInfoDto,
  IRideCancelModalPayload,
  IRideIncidentsTabCounts,
  IRideManualChangeStartEndInfo,
  IRideMovePassengersApiPayload,
  IStandardApiState,
  ITrackingInfoDto,
  ITrackingRenderState,
  RideSoftValidationErrorResponse,
  RydeUiUnit,
  TCompanyId,
  TDragRideData,
} from "../../models";

export const movePassengersErrorAtom = atom<{
  request: IRideMovePassengersApiPayload;
  response: RideSoftValidationErrorResponse;
} | null>({
  key: "movePassengersErrorAtom",
  default: null,
});

export const nearbyFilteringRideIdAtom = atom<number | null>({
  key: "filteringRideIdAtom",
  default: null,
});

export const nearbyOtherRidesIdsBatchAtom = atom<
  IStandardApiState<Record<number, number[]>>
>({
  key: "nearbyOtherRidesIdsBatchAtom",
  default: {
    isLoading: false,
    error: undefined,
    data: {},
  },
});

export const specificTableRowIdsRenderedAtom = atom<Set<number>>({
  key: "specificTableRowIdsRenderedAtom",
  default: new Set(),
});

export const ridesDraggingDataAtom = atom<TDragRideData>({
  key: "ridesDraggingDataAtom",
  default: NotDraggingDataConst,
});

/**
 * Grid single-select + map: id of the one selected ride row (not `rideMap` multi-checkbox selection).
 * Holds the id, not the dto, so row components can subscribe per row via `isSingleSelectedRideIdSF`;
 * the side map resolves the dto from the current rows via `selectedSingleRowRideDtoSelector`.
 */
export const selectedSingleRowRideIdAtom = atom<number | undefined>({
  key: "selectedSingleRowRideIdAtom",
  default: undefined,
});

export const ridesDraggingStationsTempIdsAtom = atom<
  TStationComposedId[] | undefined
>({
  key: "ridesDraggingStationsTempIdsAtom",
  default: undefined,
});

export const rideStationsCollapsedIdsAF = atomFamily<number[], number>({
  key: "rideStationsCollapsedIdsAF",
  default: [],
});

export const rideSelectedPassengersIdsAF = atomFamily<number[], number>({
  key: "rideSelectedPassengersIdsAF",
  default: [],
});

export const ridePassengerDeleteConfirmAtom =
  atom<IPassengerDeleteModalPayload | null>({
    key: "ridePassengerDeleteConfirmAtom",
    default: null,
  });

export const rideCancelConfirmAtom = atom<IRideCancelModalPayload | null>({
  key: "rideCancelConfirmAtom",
  default: null,
});

export const rideArchiveConfirmAtom = atom<IRideArchiveModalPayload | null>({
  key: "rideArchiveConfirmAtom",
  default: null,
});

export const ridePassengerDeleteBookingStateConfirmAtom = atom<
  Map<number, PassengerDeleteResultEnum>
>({
  key: "ridePassengerDeleteBookingStateConfirmAtom",
  default: new Map<number, PassengerDeleteResultEnum>([]),
});

export const rideHasUnsavedStationChangeAtom = atom<boolean>({
  key: "rideHasUnsavedStationChangeAtom",
  default: false,
});

export const lastSelectedRebuildTypeAtom = atom<RebuildRouteTypeEnum | null>({
  key: "lastSelectedRebuildTypeAtom",
  default: null,
});

export const lastRouteRebuildAtAtom = atom<TDateISO | null>({
  key: "lastRouteRebuildAtAtom",
  default: null,
});

export const ridePreviousAreasSnapshotAtom = atom<IRideAreasSnapshot | null>({
  key: "ridePreviousAreasSnapshotAtom",
  default: null,
});

export const ridePreviousMetadataSnapshotAtom = atom<IRideMetadata | null>({
  key: "ridePreviousMetadataSnapshotAtom",
  default: null,
});

export const rideHasSignificantTimeChangeAtom = atom<boolean>({
  key: "rideHasSignificantTimeChangeAtom",
  default: false,
});

export const rideEditHasMetadataChangedAtom = atom<boolean>({
  key: "rideEditHasMetadataChangedAtom",
  default: false,
});

export const rideHasNotPriceRateAtom = atom<boolean>({
  key: "rideHasNotPriceRateAtom",
  default: false,
});

export const rideTrackingAF = atomFamily<
  ITrackingInfoDto[] | undefined,
  number
>({
  key: "rideTrackingAF",
  default: undefined,
});

/**
 * Full GPS trail for the single ride shown in the edit/detail view, kept separate from the shared
 * live-map store (rideTrackingAF). The live-map poll only stores the last position (lastTrackingOnly),
 * so a single-ride view must own its full trail here, otherwise the poll would trim it away.
 */
export const editRideTrackingAF = atomFamily<
  ITrackingInfoDto[] | undefined,
  number
>({
  key: "editRideTrackingAF",
  default: undefined,
});

/** Compact per-ride GPS trail for map rendering; keyed by rideId, survives deselection. */
export const rideTrackingRenderAF = atomFamily<
  ITrackingRenderState | undefined,
  number
>({
  key: "rideTrackingRenderAF",
  default: undefined,
});

/** Live map: `new` incidents returned by incidents live-map-incidents poll for the current page entity. */
export const liveMapNewIncidentsAF = atomFamily<
  ILiveMapIncidentItem[],
  RydeUiUnit
>({
  key: "liveMapNewIncidentsAF",
  default: () => [],
});

/** Counts for ride drawer Events tab label `(new/total)`; keyed by ride id. */
export const rideIncidentsTabCountsAF = atomFamily<
  IRideIncidentsTabCounts | null,
  number
>({
  key: "rideIncidentsTabCountsAF",
  default: null,
});

export const rideBookingsAF = atomFamily<
  IRideBookingInfoDto[] | undefined,
  number
>({
  key: "rideBookingsAF",
  default: undefined,
});

export const pendingRideFetchAtom = atom<boolean>({
  key: "pendingRideFetchAtom",
  default: false,
});

/**
 * True while a ride save is in flight (set before the request, cleared in the
 * handler's `finally`). Shared so the drawer close guard can skip the
 * unsaved-changes prompt during save → close → reopen, when the ride is
 * already being persisted and no changes are actually lost.
 */
export const rideSaveInProgressAtom = atom<boolean>({
  key: "rideSaveInProgressAtom",
  default: false,
});

/**
 * True during the post-save drawer transition: from a successful save until the
 * (re)opened ride edit drawer has reloaded and the form is clean again. Bridges
 * the gap after `rideSaveInProgressAtom` is reset but before `isDirty` clears,
 * so the unsaved-changes prompt cannot fire mid-switch. Cleared by the drawer
 * once `isDirty` returns to false.
 */
export const ridePostSaveTransitionAtom = atom<boolean>({
  key: "ridePostSaveTransitionAtom",
  default: false,
});

export const rideManualDateChangeInfoAtom = atom<
  IRideManualChangeStartEndInfo | undefined
>({
  key: "rideManualDateChangeInfoAtom",
  default: undefined,
});

export const areStationsDragReorderedAtom = atom<boolean>({
  key: "areStationsDragReorderedAtom",
  default: false,
});

export const isShowWasSentApprovalMessageAtom = atom<boolean>({
  key: "isShowWasSentApprovalMessageAtom",
  default: false,
});

/** Ride drawer extra-info block; collapsed until the user expands it, then kept for the session. */
export const rideTabsBottomBlocksExpandedAtom = atom<boolean>({
  key: "rideTabsBottomBlocksExpandedAtom",
  default: false,
});

export const ridesIdSearchRefetchTokenAtom = atom<number>({
  key: "ridesIdSearchRefetchTokenAtom",
  default: 0,
});

/** Kanban card order, per company; seeded from this browser's last choice. */
export const ridesKanbanSortAF = atomFamily<RidesKanbanSortEnum, TCompanyId>({
  key: "ridesKanbanSortAF",
  default: companyId => getStoredRidesKanbanSort(companyId),
});
